/**
 * The "Open app" button group on the release card: a purple primary link and
 * a square chevron button, joined into one control. The primary link opens the
 * app by the reader's default method. The chevron opens a menu of methods;
 * choosing one makes it the default and saves it.
 *
 * It is a menu button, never a native `<select>`. The items are
 * `menuitemradio`, so assistive technology hears which one is the default.
 * Keys and outside presses follow `menuBehavior.ts`. A method that cannot
 * open (no local copy running) stays in the list, marked disabled, with a hint.
 *
 * Callers build the group only from a verified list, so no link into the app
 * exists before the signature checks out.
 */
import { createChevron } from './chevron'
import { createElement, createLink, createTextElement } from './dom'
import type { LocalCopyStorageSource } from './localCopyMemory'
import { wireMenu } from './menuBehavior'
import {
  listOpenAppMethods,
  type OpenAppContext,
  type OpenAppMethod,
  type OpenAppMethodId,
  readSavedMethod,
  resolveEffectiveMethod,
  saveMethod,
} from './openAppMethods'

let groupCount = 0

/** What the group needs from outside. */
export interface OpenAppGroupOptions {
  readonly context: OpenAppContext
  readonly storage: LocalCopyStorageSource
  /**
   * Called with the address and the method name each time the default method
   * changes, and once when the group is built. Other controls that must open
   * the same place (the app snapshot) follow the group through this, so the
   * address is worked out in one place only.
   */
  readonly onOpenTarget?: (target: OpenTarget) => void
}

/** Where the primary "Open app" link points now, and the method that chose it. */
export interface OpenTarget {
  readonly href: string
  readonly methodTitle: string
}

/** The group element and the way to tell it the local copy changed. */
export interface OpenAppGroup {
  readonly element: HTMLElement
  /** Redraws the methods after the local-copy probe settles. */
  readonly setLocalCopyRunning: (running: boolean) => void
}

/** Builds the group, closed, with the saved method as its default. */
export const createOpenAppGroup = (options: OpenAppGroupOptions): OpenAppGroup => {
  groupCount += 1
  const menuId = `open-app-menu-${groupCount}`
  let context = options.context
  let chosenId: OpenAppMethodId | null = readSavedMethod(options.storage)

  const root = createElement('div', 'open-app-group')
  const primary = createLink('#', 'Open app', 'open-app-button')
  const chevronButton = createElement('button', 'open-app-chevron')
  chevronButton.type = 'button'
  chevronButton.setAttribute('aria-haspopup', 'menu')
  chevronButton.setAttribute('aria-expanded', 'false')
  chevronButton.setAttribute('aria-controls', menuId)
  chevronButton.append(createChevron('menu'))

  const menu = createElement('div', 'to-app-menu open-app-menu')
  menu.id = menuId
  menu.hidden = true
  menu.setAttribute('role', 'menu')
  menu.setAttribute('aria-label', 'How to open the app')
  root.append(primary, chevronButton, menu)

  // One array for the life of the group: the menu wiring holds it, and
  // `refresh` replaces its contents in place.
  const items: HTMLButtonElement[] = []
  const wired = wireMenu({ root, button: chevronButton, menu, items })

  const choose = (method: OpenAppMethod): void => {
    if (method.href === null) {
      return
    }
    chosenId = method.id
    saveMethod(options.storage, method.id)
    wired.setOpen(false)
    chevronButton.focus()
    refresh()
  }

  const createItem = (method: OpenAppMethod, effectiveId: OpenAppMethodId): HTMLButtonElement => {
    const item = createElement('button', 'to-app-item open-app-item')
    item.type = 'button'
    item.dataset.method = method.id
    item.setAttribute('role', 'menuitemradio')
    item.setAttribute('aria-checked', String(method.id === effectiveId))
    item.append(
      createTextElement('span', method.title, 'to-app-title'),
      createTextElement('span', method.unavailableHint ?? method.summary, 'to-app-summary'),
    )
    if (method.href === null) {
      item.setAttribute('aria-disabled', 'true')
    }
    item.addEventListener('click', () => choose(method))
    return item
  }

  function refresh(): void {
    const methods = listOpenAppMethods(context)
    const effective = resolveEffectiveMethod(methods, chosenId)
    primary.href = effective.href
    options.onOpenTarget?.({ href: effective.href, methodTitle: effective.title })
    primary.title = `Opens the app by: ${effective.title}. Use the arrow to change it.`
    chevronButton.setAttribute('aria-label', `How to open the app. Now: ${effective.title}`)
    chevronButton.title = `How to open the app. Now: ${effective.title}`
    items.splice(0, items.length, ...methods.map((method) => createItem(method, effective.id)))
    menu.replaceChildren(...items)
  }
  refresh()

  return {
    element: root,
    setLocalCopyRunning: (running) => {
      context = { ...context, localCopyRunning: running }
      refresh()
    },
  }
}
